/**
 * Configuration centralisée des environnements
 * Fusionne la logique de validation et les exports d'environnement
 */

export interface EnvironmentConfig {
  functionsBaseUrl: string;
  projectId: string;
  region: string;
  googleMapsApiKey: string;
  isDevelopment: boolean;
  isProduction: boolean;
  mode: 'development' | 'staging' | 'production';
}

/**
 * Variables d'environnement requises
 */
const REQUIRED_ENV_VARS = {
  VITE_FIREBASE_FUNCTIONS_URL: 'URL de base des Cloud Functions',
  VITE_FIREBASE_PROJECT_ID: 'ID du projet Firebase',
  VITE_FIREBASE_REGION: 'Région des Cloud Functions'
} as const;

/**
 * Valide la présence de toutes les variables d'environnement requises
 */
function validateEnvironment(): void {
  const missing: string[] = [];

  Object.entries(REQUIRED_ENV_VARS).forEach(([key, description]) => {
    if (!import.meta.env[key]) {
      missing.push(`${key} (${description})`);
    }
  });

  if (missing.length > 0) {
    const errorMessage = `
❌ ERREUR CRITIQUE : Variables d'environnement manquantes

Variables requises manquantes:
${missing.map(v => `  - ${v}`).join('\n')}

Veuillez créer un fichier .env à la racine du projet avec:
VITE_FIREBASE_FUNCTIONS_URL=https://[region]-[project-id].cloudfunctions.net
VITE_FIREBASE_PROJECT_ID=[project-id]
VITE_FIREBASE_REGION=[region]

Exemple pour cashlive-develop:
VITE_FIREBASE_FUNCTIONS_URL=https://europe-west1-cashlive-develop.cloudfunctions.net
VITE_FIREBASE_PROJECT_ID=cashlive-develop
VITE_FIREBASE_REGION=europe-west1
    `;

    console.error(errorMessage);
    throw new Error('Variables d\'environnement manquantes. Voir la console pour plus de détails.');
  }
}

/**
 * Récupère la configuration de l'environnement avec validation
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  // Valider les variables d'environnement
  validateEnvironment();

  const functionsBaseUrl = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL!;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID!;
  const region = import.meta.env.VITE_FIREBASE_REGION!;
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  const isDevelopment = import.meta.env.DEV === true ||
    import.meta.env.MODE === "development" ||
    (typeof window !== "undefined" && (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.port === "5173"
    ));

  const isProduction = !isDevelopment;

  // Déterminer le mode basé sur le project ID
  let mode: 'development' | 'staging' | 'production' = 'development';
  if (projectId.includes('prod')) {
    mode = 'production';
  } else if (projectId.includes('staging')) {
    mode = 'staging';
  }

  const config: EnvironmentConfig = {
    functionsBaseUrl,
    projectId,
    region,
    googleMapsApiKey,
    isDevelopment,
    isProduction,
    mode
  };

  // Log de la configuration (uniquement en dev)
  if (isDevelopment) {
    console.log('🌍 Configuration de l\'environnement:', {
      functionsBaseUrl,
      projectId,
      region,
      mode,
      googleMapsApiKey: googleMapsApiKey ? '✅ Définie' : '⚠️ Manquante'
    });
  }

  return config;
}

// Export de la configuration singleton
let config: EnvironmentConfig | null = null;

export function getConfig(): EnvironmentConfig {
  if (!config) {
    config = getEnvironmentConfig();
  }
  return config;
}

// Exports directs pour usage simple (avec validation)
export const FUNCTIONS_BASE_URL = getConfig().functionsBaseUrl;
export const PROJECT_ID = getConfig().projectId;
export const REGION = getConfig().region;
export const GOOGLE_MAPS_API_KEY = getConfig().googleMapsApiKey;
export const IS_DEVELOPMENT = getConfig().isDevelopment;
export const IS_PRODUCTION = getConfig().isProduction;
export const MODE = getConfig().mode;

// Export de l'objet environment pour compatibilité avec l'ancien code
export const environment = {
  isDevelopment: IS_DEVELOPMENT,
  mode: MODE,
  devProjectId: "cashlive-develop",
  prodProjectId: "cashlive-f1e1e",
};

export const getEnvironmentInfo = () => {
  const cfg = getConfig();
  return {
    environment: cfg.isDevelopment ? "DEVELOPMENT" : "PRODUCTION",
    projectId: cfg.projectId,
    hostname: typeof window !== "undefined" ? window.location.hostname : "unknown",
    port: typeof window !== "undefined" ? window.location.port : "unknown",
    mode: cfg.mode,
    functionsBaseUrl: cfg.functionsBaseUrl,
    region: cfg.region,
  };
};

// Export default
export default getConfig();
